/* eslint-disable no-console */

export type ApiFetchOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
  baseUrl: string;
  name: string;
  cache?: RequestCache;
  next?: {
    tags?: string[];
    revalidate?: number | false;
  };
};

const isAbortError = (
  error: unknown,
): error is Error & { name: "AbortError" } => {
  return error instanceof Error && error.name === "AbortError";
};

const logError = (message: string, error?: unknown, meta?: object) => {
  if (typeof console !== "undefined" && console.error) {
    console.error(message, error, meta);
  }
};

export default async function apiFetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
  baseUrl,
  name,
  cache,
  next,
}: ApiFetchOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  try {
    const requestHeaders: HeadersInit = {
      "Content-Type": "application/json",
      ...headers,
    };

    if (
      requestHeaders["Content-Type"] &&
      requestHeaders["Content-Type"]
        .toLowerCase()
        .includes("multipart/form-data")
    ) {
      delete requestHeaders["Content-Type"];
    }

    const path = resolveUrl(url, queryParams, pathParams);
    const response = await fetch(`${baseUrl}${path}`, {
      signal,
      method: method.toUpperCase(),
      body: body
        ? body instanceof FormData
          ? body
          : JSON.stringify(body)
        : undefined,
      headers: requestHeaders,
      cache,
      next,
    } as RequestInit);

    if (response.status === 204) return null as TData;

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch from ${name} api ${path} with status ${response.status}`,
      ) as Error & { cause: Record<string, unknown> & { status: number } };

      if (response.headers.get("content-type")?.includes("json")) {
        try {
          const cause = await response.json();
          error.cause = {
            ...cause,
            status: response.status,
          };
        } catch (parseErr) {
          logError("Failed to parse json", parseErr);
        }
      } else {
        error.cause = { status: response.status };
      }

      throw error;
    }

    if (response.status === 404) {
      const error = new Error(`Not found on ${name} api`);
      (error as Error & { cause: Response }).cause = response;
      throw error;
    }

    if (response.headers.get("content-type")?.includes("json")) {
      return await response.json();
    } else {
      const blob = (await response.blob()) as unknown as TData;
      if (blob instanceof Blob && blob.size === 0) {
        return undefined as TData;
      }
      return blob;
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    logError("Failed to fetch!", error, {
      path: resolveUrl(url, queryParams, pathParams),
    });
    throw error;
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string> = {},
  pathParams: Record<string, string> = {},
) => {
  let query = new URLSearchParams(queryParams).toString();
  if (query) query = `?${query}`;
  return (
    url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)] || "") + query
  );
};
